"use client";

import * as React from "react";
import { useState } from "react";
import { IconChevronDown, IconXFilled } from "@tabler/icons-react";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TruncatedText } from "@/components/ui/truncated-text";
import { cn } from "@/lib/utils";

/**
 * Sentinel id for the opt-in single-mode clear row.
 *
 * It is NOT a reserved id: `member-select`, `event-parent-select` and
 * `category-select` each predate `clearable` and inject their own option with
 * this exact literal. So every read of it below is gated on `clearable` — a
 * caller that has not opted in keeps full ownership of a row with this id, its
 * own `renderOption` and its own null-mapping. Drop that gate and those three
 * silently start rendering this component's clear row instead of theirs.
 */
const CLEAR_OPTION_ID = "__clear__";

export interface CommandSelectOption {
  id: string;
  /** Display label. Search matches against this (and `keywords`). */
  name: string;
  /** Extra terms to match in search beyond `name`. */
  keywords?: string[];
  disabled?: boolean;
  /** Why this option is disabled, shown as a tooltip on hover/focus.
   *
   *  A locked option with no stated reason reads as a bug. Set it whenever the
   *  lock is a rule the user could otherwise satisfy — "already covered by X",
   *  "a workflow may belong to one SLA" — so the dead end explains itself
   *  instead of being discovered at save time. Ignored unless `disabled`.
   *
   *  Prefer {@link CommandSelectOption.disabledNote} when the reason fits in a
   *  couple of words: a note needs no hover and hides nothing. This stays the
   *  right field for a reason that only says itself in a sentence. */
  disabledReason?: string;
  /** A two-or-three-word reason, rendered ON the row instead of in a tooltip.
   *
   *  Takes precedence over `disabledReason` — a row that already states its
   *  reason has no use for a bubble repeating it. Prefer this: the tooltip is
   *  anchored to a full-width wrapper around the row (a disabled `CommandItem`
   *  takes no pointer events, so something has to catch the hover), which means
   *  it either points at empty space beside a short name or covers the option
   *  above it. A note has neither problem, needs no hover, and reads before the
   *  user reaches for it.
   *
   *  Keep it to a fragment — "sub-team", "already used", "this team". It shares
   *  the row with the name, so a sentence truncates it. It also joins the row's
   *  accessible name, which is the point: a screen-reader user hears the reason
   *  with the option rather than having to hunt a tooltip. Ignored unless
   *  `disabled`. */
  disabledNote?: string;
}

/** What `renderOptionAction` gets beside the option it renders for. */
export interface CommandSelectOptionActionContext {
  /**
   * Move focus to the list's search box (a no-op without one). For an action
   * that re-renders itself away when used, e.g. a "Set home" chip that becomes
   * an inert "Home" pill: the focus it held would otherwise drop to <body>,
   * which also stops typing from filtering the list.
   */
  focusSearch: () => void;
}

interface CommandSelectBaseProps {
  disabled?: boolean;
  placeholder: string;
  searchPlaceholder?: string;
  emptyLabel: string;
  triggerAriaLabel: string;
  /**
   * Whether to show the search input.
   *
   * Search is shown by default — most dropdowns are API-backed and may have more
   * results than are currently rendered. Pass `false` ONLY for a known, complete
   * static list under ~6 items.
   */
  searchable?: boolean;
  /**
   * Controlled search text. Provide together with `onSearchChange` for
   * API-backed selects that filter server-side; omit for local cmdk filtering.
   */
  search?: string;
  onSearchChange?: (next: string) => void;
  /**
   * Opt into a fixed width (px) for the trigger; the list matches it. When
   * omitted, the trigger fills its container (`w-full`) — the typical fixed
   * value per the design spec is 280.
   */
  width?: number;
  /** Forwarded to the trigger so an external `<Label htmlFor>` can associate. */
  id?: string;
  className?: string;
  /**
   * Render the label content of an option (e.g. an avatar + name). Receives the
   * option. When omitted, the option's `name` is rendered as a truncating span
   * that reveals the full value in a tooltip only when actually clipped. A
   * custom renderer is responsible for its own truncation.
   */
  renderOption?: (option: CommandSelectOption) => React.ReactNode;
  /**
   * Override the trigger's selected-value text. Receives the currently selected
   * options. When omitted, the trigger joins the selected names with ", " (and
   * shows `placeholder` when nothing is selected). Use this for an "N selected"
   * style summary.
   */
  renderTriggerLabel?: (selected: CommandSelectOption[]) => React.ReactNode;
}

interface CommandSelectSingleProps extends CommandSelectBaseProps {
  mode?: "single";
  options: CommandSelectOption[];
  value: string | null;
  onChange: (next: string | null) => void;
  /**
   * Render a leading row that emits `null`, shown only while a value is set
   * (NG-1017). Single mode only — a multi selection clears by unchecking.
   *
   * OFF by default, because this select also drives pickers that must never go
   * empty: `currency-select` and `timezone-select` coerce `null` to `""`, so a
   * clear row would silently write an empty workspace setting, and the Branch /
   * Review decision-option statuses are nullable in the model but marked
   * `required` in their config field.
   *
   * Takes effect only together with `clearLabel`. The two stay independent
   * optionals rather than a discriminated pair because every real caller is a
   * forwarding wrapper (`EntitySelect`, `MemberSelect`, …) holding its own
   * optional props, which a union shape cannot satisfy without each wrapper
   * re-deriving it.
   */
  clearable?: boolean;
  /**
   * The clear row's only text, in the caller's own words ("No status").
   * Without it there is no row at all — a labelless row would read as a
   * rendering bug, and its absence tells the author immediately.
   */
  clearLabel?: string;
}

interface CommandSelectMultiProps extends CommandSelectBaseProps {
  mode: "multi";
  options: CommandSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * How the trigger renders the selection.
   *
   * - `"text"` (default) — the selected names joined with ", ", on one
   *   truncating line. Right when the control is one field in a dense form and
   *   the selection is usually short.
   * - `"chips"` — one `Badge` per selection, wrapping onto as many rows as it
   *   takes. Right when the selection IS the content of the surface and a
   *   reader is meant to take it in at a glance (a person's skills, a set of
   *   tags), where a truncated comma list hides most of the answer.
   *
   * Multi only: a single selection has nothing to wrap. `renderTriggerLabel`
   * takes precedence over both — it is the fully-custom escape hatch.
   */
  trigger?: "text" | "chips";
  /**
   * A small control rendered at the right end of an option's row — e.g. a
   * "Set home" chip that designates one member of the selection (NG-2161).
   * Return `null` for a row that needs none.
   *
   * It is a SIBLING of the option, never inside it: the option's accessible
   * name stays exactly its `name` (every `getByRole("option", {name, exact})`
   * locator depends on that), and a control nested in an option is not
   * operable. It keeps its own tab stop, and its clicks and Enter/Space never
   * reach the option or cmdk, so it neither toggles the row nor closes the list.
   * Give it its own accessible name that names the row ("Set home: Design").
   *
   * The wrapper around it is the option's `aria-describedby`, so what the pill
   * says reaches a screen reader with the option ("Design, checked, Home") even
   * though cmdk only announces the option itself through `aria-activedescendant`.
   *
   * The second argument hands focus back to the list (see
   * {@link CommandSelectOptionActionContext}), so a caller never reaches into
   * cmdk's DOM for it.
   */
  renderOptionAction?: (
    option: CommandSelectOption,
    context: CommandSelectOptionActionContext,
  ) => React.ReactNode;
  /** Content pinned under the option list, inside the popover — a short note
   *  about a rule the list enforces. Muted, small, above nothing else. */
  listFooter?: React.ReactNode;
}

export type CommandSelectProps = CommandSelectSingleProps | CommandSelectMultiProps;

/**
 * The shared searchable dropdown base for the app's Group A selects — single and
 * multi. Built on Popover + Command (cmdk).
 *
 * Behavior:
 * - `mode="single"` — checkmark on the right marks the selection; closes on select.
 * - `mode="multi"` — checkbox on the left; toggles; stays open on select.
 * - Search shown by default (API-backed assumption); pass `searchable={false}`
 *   only for a known complete static list under ~6 items.
 * - The list is never narrower than the trigger and grows to fit long option
 *   labels, capped at the standard popover width (18rem) so a narrow trigger
 *   (e.g. a sidebar field) still gets readable options. Values longer than the
 *   cap truncate and reveal the full value in a tooltip. A fixed `width` pins
 *   the list to it.
 * - Scroll is automatic: `CommandList` caps its height and scrolls on overflow.
 *
 * When `search`/`onSearchChange` are provided, cmdk's local filtering is disabled
 * (`shouldFilter={false}`) so server-side results pass through untouched.
 */
// eslint-disable-next-line mobx/missing-observer -- wrapped via observer(...) below
function CommandSelectInner(props: CommandSelectProps) {
  const {
    options,
    disabled,
    placeholder,
    searchPlaceholder,
    emptyLabel,
    triggerAriaLabel,
    searchable = true,
    search,
    onSearchChange,
    width,
    id,
    className,
    renderOption,
    renderTriggerLabel,
  } = props;
  const isMulti = props.mode === "multi";
  const isChips = isMulti && props.trigger === "chips";
  const renderOptionAction = isMulti ? props.renderOptionAction : undefined;
  const listFooter = isMulti ? props.listFooter : undefined;
  const [open, setOpen] = useState(false);
  // Ids for the `renderOptionAction` wrappers, each the description of its option.
  const actionIdBase = React.useId();

  // Snapshot plain option data in the observer render. cmdk's filtering + the
  // popover content run outside the observer's reactive scope, so reading
  // observable fields there would trip `observableRequiresReaction` per item.
  const opts = options.map((o) => ({
    id: o.id,
    name: o.name,
    keywords: o.keywords ? [...o.keywords] : undefined,
    disabled: o.disabled,
    disabledReason: o.disabledReason,
    disabledNote: o.disabledNote,
  }));

  // Our clear row, or null when there is none to show. Present only while a
  // value is set — with nothing selected there is nothing to clear — and keyed
  // off the RAW value rather than `hasSelection` below, so a value whose option
  // hasn't loaded yet is still clearable instead of stranded behind the
  // placeholder.
  const clearRow: CommandSelectOption | null =
    !isMulti && props.clearable && props.clearLabel && props.value !== null
      ? { id: CLEAR_OPTION_ID, name: props.clearLabel }
      : null;
  const showClearRow = clearRow !== null;

  // Rows in list order, our row first. It stays out of `opts` so the trigger
  // label, the selection lookup and a caller's `renderOption` only ever see
  // real options.
  const rows: CommandSelectOption[] = clearRow ? [clearRow, ...opts] : opts;

  const selectedIds = isMulti
    ? new Set((props.value as string[]) ?? [])
    : new Set(props.value ? [props.value as string] : []);

  const selectedOptions = opts.filter((o) => selectedIds.has(o.id));
  // Drive the trigger off resolvable selections, not raw ids: a value whose
  // options are absent has no name to render, so the trigger falls back to the
  // placeholder (and keeps its aria-label) rather than showing an empty control.
  //
  // KNOWN MISRENDER (NG-1095): that fallback conflates "nothing is selected" with
  // "something is selected but we cannot name it yet", so a saved value whose list
  // is still loading reads as data loss. Callers that feed an async list must gate
  // on their own loading signal — see ``useAvailableFields`` and
  // ``ChangeOwnerConfigPanel``. Teaching this component the three-state
  // distinction, so every caller gets it for free, is tracked separately.
  const hasSelection = selectedOptions.length > 0;
  const triggerLabel = (() => {
    if (renderTriggerLabel) return renderTriggerLabel(selectedOptions);
    if (!hasSelection) return placeholder;
    if (isChips) {
      return (
        <span className="flex flex-wrap items-center gap-1">
          {selectedOptions.map((o) => (
            // `Badge` is `shrink-0 whitespace-nowrap`, so a long option would
            // otherwise render wider than the trigger and spill out of it.
            // `max-w-full` caps the chip at the control and `TruncatedText`
            // clips the label inside it — revealing the full value on hover
            // only when it is genuinely cut off.
            <Badge key={o.id} variant="secondary" className="max-w-full">
              <TruncatedText text={o.name} className="min-w-0" />
            </Badge>
          ))}
        </span>
      );
    }
    return selectedOptions.map((o) => o.name).join(", ");
  })();

  const handleSelect = (optId: string) => {
    if (isMulti) {
      // `props.value` is frequently an observable array and this runs from an
      // event, i.e. outside the observer's reaction — so the read goes through
      // `runInAction` (mobx's dev-mode `observableRequiresReaction` guard warns
      // otherwise). Same toggle, same emitted array.
      const next = runInAction(() => {
        const current = (props.value as string[]) ?? [];
        return current.includes(optId) ? current.filter((x) => x !== optId) : [...current, optId];
      });
      (props.onChange as (n: string[]) => void)(next);
    } else {
      // OUR clear row resets to null; every real id — including a caller's own
      // `__clear__` option when it hasn't opted in — passes through untouched.
      const isOurClearRow = showClearRow && optId === CLEAR_OPTION_ID;
      (props.onChange as (n: string | null) => void)(isOurClearRow ? null : optId);
      setOpen(false);
    }
  };

  // Server-side search => disable cmdk's local filter so results pass through.
  const controlledSearch = search !== undefined;

  return (
    <Popover open={!disabled && open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            // Stable accessible name (the field label), regardless of selection —
            // the conventional combobox pattern. The selected value is conveyed
            // by the trigger's visible text content, not by mutating the name,
            // so the control stays findable by its label after a selection.
            aria-label={triggerAriaLabel}
            style={width !== undefined ? { width } : undefined}
            className={cn(
              "justify-between gap-1.5 px-2 text-left text-xs/relaxed",
              // Chips wrap, so the box grows instead of clipping; it still
              // starts at the standard 28px control height when one row fits.
              isChips ? "h-auto min-h-7 items-start py-1" : "h-7",
              width === undefined && "w-full",
              className,
            )}
            disabled={disabled}
          >
            <span
              className={cn(
                "min-w-0 flex-1",
                !isChips && "truncate",
                !hasSelection && "text-muted-foreground",
              )}
            >
              {triggerLabel}
            </span>
            <IconChevronDown
              className={cn(
                "size-3.5 shrink-0 text-muted-foreground",
                // Keep the chevron on the first row when the chips wrap.
                isChips && "mt-1",
              )}
            />
          </Button>
        }
      />
      <PopoverContent
        align="start"
        size="auto"
        className="max-w-72 min-w-(--anchor-width) p-0"
        style={width !== undefined ? { maxWidth: width } : undefined}
      >
        <Command shouldFilter={!controlledSearch}>
          {searchable && (
            <CommandInput
              // i18n-exempt: components/ui may not import i18n (dependency-cruiser); callers pass a translated searchPlaceholder
              placeholder={searchPlaceholder ?? "Search..."}
              value={search}
              onValueChange={onSearchChange}
            />
          )}
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {rows.map((o) => {
                const isChecked = selectedIds.has(o.id);
                const isClearRow = showClearRow && o.id === CLEAR_OPTION_ID;
                const item = (
                  <CommandItem
                    // Append the id so duplicate names stay distinct to cmdk;
                    // filtering still matches the name/keyword substrings.
                    key={o.id}
                    value={`${o.name} ${o.id}`}
                    keywords={o.keywords}
                    disabled={o.disabled}
                    // A noted row stays HOVERABLE, overriding `CommandItem`'s
                    // `data-[disabled=true]:pointer-events-none`. That is what lets
                    // a clipped long name reveal itself through `TruncatedText`'s
                    // own tooltip — above the row, with an arrow — which a locked
                    // row could not do before, leaving a truncated name
                    // unreadable with no way to get at it.
                    //
                    // Safe because cmdk blocks selection in JS, not in CSS: it
                    // passes `onClick: undefined` and skips `onPointerMove` for a
                    // disabled item, so the row cannot be chosen or become the
                    // active one however it is hovered. Only the noted rows get
                    // this — a `disabledReason` row keeps its wrapper tooltip, and
                    // making it hoverable too would fire two bubbles at once.
                    //
                    // `!` is load-bearing: `CommandItem`'s rule is
                    // `data-[disabled=true]:pointer-events-none`, a class PLUS
                    // attribute selector, so it outranks a plain
                    // `pointer-events-auto` whatever the source order — the class
                    // applies and loses, silently. Playwright is what surfaced it
                    // ("cmdk-group-items intercepts pointer events"); a unit test
                    // asserting the class is present cannot, since no stylesheet
                    // is loaded to resolve the cascade.
                    className={o.disabled && o.disabledNote ? "pointer-events-auto!" : undefined}
                    // `data-checked` drives CommandItem's built-in right-side tick
                    // (single mode). Multi mode renders its own left checkbox.
                    data-checked={!isMulti && isChecked ? true : undefined}
                    // Multi mode announces its own checked state. Its only
                    // indicator is the `aria-hidden` Checkbox below, so without
                    // this a screen reader hears a plain list — and nothing can
                    // read the selection back, which is the same defect
                    // `DataViewFilterForm` carried. ARIA 1.2 permits
                    // `aria-checked` on `option`. Single mode is left alone: it
                    // is a value picker, where the chosen row is announced by the
                    // trigger, and `aria-selected` there would be cmdk's keyboard
                    // highlight rather than the choice.
                    aria-checked={isMulti ? isChecked : undefined}
                    onSelect={() => handleSelect(o.id)}
                  >
                    {isMulti && (
                      <Checkbox
                        checked={isChecked}
                        // The CommandItem owns selection; the checkbox is purely
                        // visual so it must not also fire on the same click.
                        tabIndex={-1}
                        aria-hidden
                        className="pointer-events-none"
                      />
                    )}
                    {isClearRow ? (
                      // The clear row is ours, not the caller's, so it skips
                      // `renderOption` — an avatar/dot renderer has nothing to
                      // draw for "no selection" and would have to special-case
                      // the sentinel to avoid it.
                      <>
                        <IconXFilled className="size-3.5 shrink-0 text-muted-foreground" />
                        <TruncatedText text={o.name} className="min-w-0 flex-1" />
                      </>
                    ) : renderOption ? (
                      renderOption(o)
                    ) : (
                      <TruncatedText text={o.name} className="min-w-0 flex-1" />
                    )}
                    {o.disabled && o.disabledNote ? (
                      <>
                        {/* A real whitespace node, not the flex `gap`: the note joins
                            the row's accessible name, and CSS gap contributes
                            nothing to it — without this a reader announces
                            "Design Systemsub-team" as one run. */}{" "}
                        {/* `shrink-0` so the reason survives a long name — the name
                            truncates instead, which is the right sacrifice: the note
                            is short, fixed, and what the row is there to explain. */}
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {o.disabledNote}
                        </span>
                      </>
                    ) : null}
                  </CommandItem>
                );

                // The action wrapper's id is also how `focusSearch` finds this
                // list's search box: from inside the same cmdk root.
                const actionId = `${actionIdBase}-action-${o.id}`;
                const focusSearch = () =>
                  document
                    .getElementById(actionId)
                    ?.closest("[cmdk-root]")
                    ?.querySelector<HTMLElement>("[cmdk-input]")
                    ?.focus();
                const action =
                  renderOptionAction && !isClearRow ? renderOptionAction(o, { focusSearch }) : null;
                if (action) {
                  // The action sits BESIDE the option (see `renderOptionAction`).
                  // cmdk unmounts a filtered-out item but not this wrapper, so the
                  // wrapper shows only while it still holds its item — otherwise
                  // a search would leave orphan actions behind.
                  const stop = (event: React.SyntheticEvent) => event.stopPropagation();
                  const locked = o.disabled && o.disabledReason && !o.disabledNote;
                  // The action describes the option (see `renderOptionAction`): a
                  // description, not a name, so `{name, exact}` still resolves.
                  const described = React.cloneElement(item, { "aria-describedby": actionId });
                  return (
                    <div
                      key={o.id}
                      className="hidden items-center gap-1 pr-1 has-[[data-slot=command-item]]:flex"
                    >
                      <div className={cn("min-w-0 flex-1", locked && "cursor-not-allowed")}>
                        {locked ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger render={<div />}>{described}</TooltipTrigger>
                              <TooltipContent>{o.disabledReason}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          described
                        )}
                      </div>
                      {/* Stops the click and the activation keys here: cmdk
                          selects its active item on Enter at the list root. */}
                      <div
                        id={actionId}
                        className="shrink-0"
                        onClick={stop}
                        onPointerDown={stop}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") event.stopPropagation();
                        }}
                      >
                        {action}
                      </div>
                    </div>
                  );
                }

                // A note is already on the row, so a bubble repeating it would be
                // noise — and the note is why `disabledNote` is the field to reach
                // for (see its doc).
                if (!o.disabled || !o.disabledReason || o.disabledNote) return item;
                // `Tooltip.Root` is inert without a Provider ancestor — unwrapped,
                // the trigger renders but never opens. The trigger is a wrapper
                // element rather than the item itself: a disabled `CommandItem`
                // takes no pointer events, so hovering the row would otherwise
                // never reach it.
                //
                // That wrapper is the row's FULL width, so this bubble cannot hug a
                // short name whatever alignment it is given — it points at empty
                // space beside one. Deliberately left as it was rather than nudged:
                // every remaining caller of `disabledReason` is outside this
                // feature's flag, and moving their tooltips is not this change's to
                // make. Fixing it properly is what `disabledNote` does.
                return (
                  <TooltipProvider key={o.id}>
                    <Tooltip>
                      <TooltipTrigger render={<div />}>{item}</TooltipTrigger>
                      <TooltipContent>{o.disabledReason}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </CommandGroup>
          </CommandList>
          {listFooter ? (
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">{listFooter}</div>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const CommandSelect = observer(CommandSelectInner);
